/**
 * Readiness helper for the global Stripe.js library.
 *
 * Stripe.js is loaded on every page (Radar's fraud signals need it sitewide),
 * but public/index.html injects it only once the page has loaded and the
 * browser is idle — so `window.Stripe` does NOT exist when the app bundle runs
 * or when React hydrates. public/index.html defines `window.stripeReady`, a
 * promise that resolves with the Stripe constructor (or null on load failure).
 *
 * Code that needs Stripe.js must await this instead of reading `window.Stripe`
 * synchronously.
 */

/**
 * Resolves with the `window.Stripe` constructor once Stripe.js has executed,
 * or with `null` when Stripe could not be loaded (and on the server) — callers
 * decide how to degrade.
 *
 * @returns {Promise<Function|null>}
 */
export const waitForStripe = () => {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (window.Stripe) return Promise.resolve(window.Stripe);
  if (window.stripeReady && typeof window.stripeReady.then === 'function') {
    return window.stripeReady.then(() => window.Stripe || null);
  }
  // No readiness promise (tests, storybook, legacy HTML): report what exists.
  return Promise.resolve(window.Stripe || null);
};
